import { action, computed, observable, reaction, makeObservable } from "mobx";
import moment from 'moment';
import { CategoriaMinuta, ControleGabinete, IEtiqueta, IIndicadores, IUsuario, Pauta, TAG_ALGUMA_ETIQUETA, TAG_NENHUMA_ETIQUETA, TipoMinuta } from "../modelo";
import { IAutocomplete } from "../modelo/Autocomplete";
import { FiltroTriagem } from "../modelo/Triagem";
import { UnidadePublicadora } from "../modelo/UnidadePublicadora";
import ControleGabineteStore from "./ControleGabineteStore";
import EscaninhoStore from "./EscaninhoStore";
import GabineteStore from "./GabineteStore";
import MovimentoCNJStore from "./MovimentoCNJStore";
import ParametroStore from "./ParametroStore";
import ProcessoStore from "./ProcessoStore";

/**
 * Listas de opções para autocomplete dos filtros do escaninho (classe, tema, pauta,
 * movimento, OJ, situação de minuta, etiqueta, partes, assuntos...), derivadas dos
 * processos carregados. Extraído do FiltroStore (que consome estes getters via fachada)
 * para separar "montar listas de opções" de "estado/predicados de filtro selecionado".
 */
class FiltroAutoCompleteStore {
    unidadesDePublicacao: UnidadePublicadora[] = [];

    private disposeReactionUnidades?: () => void;

    constructor() {
        makeObservable(this, {
            unidadesDePublicacao: observable,
            atualizarUnidades: action,
            classesUnicasFormatoAutoComplete: computed,
            temasUnicosFormatoAutoComplete: computed,
            pautasUnicasFormatoAutoComplete: computed,
            movimentos: computed,
            resultadoJulgamento: computed,
            categoriasUnicasFormatoAutoComplete: computed,
            cpfsUltimaAlteracaoSituacaoMinutaAutocomplete: computed,
            ojAutoComplete: computed,
            situacaoMinutaFormatoAutoComplete: computed,
            unidadePublicadoraFormatoAutoComplete: computed,
            etiquetasUnicasParaAutoComplete: computed,
            tiposPeticaoPendenteAutocomplete: computed,
            partesUnicosFormatoAutoComplete: computed,
            impedimentosAutocomplete: computed,
            camposDeTriagemBooleanFormatoAutoComplete: computed,
            tiposMinutaAssinada: computed,
            camposDeResponsavelPelaTriagemAutoComplete: computed,
            assuntosUnicosFormatoAutoComplete: computed,
            tarefasAutoComplete: computed,
            usuariosAutoComplete: computed,
            relatorAutoComplete: computed,
            indicadoresAutoComplete: computed,
            andTramTarefas: computed,
            anoAutuacao: computed,
        });

        // Avoid circular-init TDZ with GabineteStore by deferring this reaction setup.
        import('./GabineteStore').then(({ default: GabineteStore }) => {
            this.disposeReactionUnidades = reaction(
                () => GabineteStore.unidadesDePublicacao,
                (novasUnidades) => {
                    this.atualizarUnidades(novasUnidades || []);
                }
            );
        });
    }

    get classesUnicasFormatoAutoComplete() {
        const classes: IAutocomplete<string>[] = [];
        try {
            EscaninhoStore.processosPorTipoEscaninho.forEach(proc => {
                const classe = proc.classes ? proc.classes.split('-')[0].trim() : proc.classe;
                if (classe) {
                    classes[classe] = {
                        texto: classe,
                        parametrosBusca: [classe],
                        valor: classe
                    };
                } else {
                    console.log('enfiou um undefined? ', classe, proc)
                }
            });
            const ret = Object.values(classes).sort((a, b) => a.texto.localeCompare(b.texto));
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get temasUnicosFormatoAutoComplete(): IAutocomplete<string>[] {
        const temasUnicos: IAutocomplete<string>[] = [];
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processoslength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processoslength; i++) {
                const proc = processos[i];
                const temas = proc.temas;
                const temasLength = temas.length;
                for (let j = 0; j < temasLength; j++) {
                    const tema = temas[j];
                    temasUnicos[tema.codTema] = {
                        texto: tema.desTema,
                        parametrosBusca: [tema.codTema.toString(), tema.desTema],
                        valor: tema.desTema
                    };
                }
            }
            return Object.values(temasUnicos);
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get pautasUnicasFormatoAutoComplete(): IAutocomplete<Pauta>[] {
        const pautas: IAutocomplete<Pauta>[] = [];

        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = processos.length;

            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                const pauta = proc.pauta;
                if (!pauta) {
                    continue;
                }

                const dataSessao = pauta.dataSessao;
                const idPauta = pauta.idPauta;
                pautas[idPauta] = {
                    texto: idPauta,
                    textoSecundario: moment(dataSessao).format('DD/MM/YYYY - HH:mm'),
                    parametrosBusca: [idPauta, dataSessao],
                    valor: pauta
                };
            }
            const ret = Object.values(pautas).sort((a, b) => {
                const dateA = a && a.valor && a.valor.dataSessao ? moment(a.valor.dataSessao).valueOf() : 0;
                const dateB = b && b.valor && b.valor.dataSessao ? moment(b.valor.dataSessao).valueOf() : 0;
                return dateB - dateA;
            });
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get movimentos(): IAutocomplete<string>[] {
        const movimentos = new Map<string, IAutocomplete<string>>();
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho || [];
            const processosLength = processos.length;

            // Indexa descricoes por codigo para evitar find() em loop aninhado.
            const descricaoPorCodigo = new Map<string, string>();
            const movimentosStore = MovimentoCNJStore.movimentos || [];
            const movimentosStoreLength = movimentosStore.length;
            for (let i = 0; i < movimentosStoreLength; i++) {
                const movimentoStore = movimentosStore[i];
                const codigo = movimentoStore?.codMovimentoCnj;
                if (!codigo) continue;

                const codigoTexto = codigo.toString();
                const descricao = movimentoStore?.descricaoMovimento || movimentoStore?.descricaoMovimentoCnj || '';
                descricaoPorCodigo.set(codigoTexto, descricao);
            }

            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                const minutas = proc?.minutas || [];

                for (let j = 0; j < minutas.length; j++) {
                    const minuta = minutas[j];
                    const movimentosMinuta = minuta?.movimentosCNJ || [];

                    for (let k = 0; k < movimentosMinuta.length; k++) {
                        const movimentoMinuta = movimentosMinuta[k];
                        const codigo = movimentoMinuta?.codMovimentoCnj;
                        if (!codigo) continue;

                        const codigoTexto = codigo.toString();
                        if (movimentos.has(codigoTexto)) continue;

                        const descricaoMovimentoCnj =
                            descricaoPorCodigo.get(codigoTexto)
                            || movimentoMinuta?.descricaoMovimento
                            || movimentoMinuta?.descricaoMovimentoUnificacao
                            || '';

                        const texto = descricaoMovimentoCnj
                            ? `${codigoTexto} - ${descricaoMovimentoCnj}`
                            : codigoTexto;

                        movimentos.set(codigoTexto, {
                            texto,
                            parametrosBusca: descricaoMovimentoCnj
                                ? [codigoTexto, descricaoMovimentoCnj, texto]
                                : [codigoTexto],
                            valor: codigoTexto
                        });
                    }
                }
            }

            return Array.from(movimentos.values());
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get resultadoJulgamento(): IAutocomplete<string>[] {
        const mapa = new Map<string, IAutocomplete<string>>();

        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                if (!proc.mostrar) continue;

                const resultado = proc.resultadoJulgamentoProcesso?.tipoSituacaoPautaDto?.tipoSituacaoPauta;

                if (resultado === "Aguardando Sessão de Julgamento") continue;

                if (resultado && !mapa.has(resultado)) {
                    mapa.set(resultado, {
                        texto: resultado,
                        parametrosBusca: [resultado],
                        valor: resultado
                    });
                }
            }

            return [...mapa.values()];
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get categoriasUnicasFormatoAutoComplete(): IAutocomplete<CategoriaMinuta>[] {
        const categorias: IAutocomplete<CategoriaMinuta>[] = [];

        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                proc.minutas.forEach((minuta) => {
                    minuta.categorias.forEach((categoria) => {
                        categorias[categoria.descricao] = {
                            texto: categoria.descricao,
                            parametrosBusca: [categoria.descricao],
                            valor: categoria
                        };
                    })
                })
            }
            const cat = Object.values(categorias);
            return cat;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get cpfsUltimaAlteracaoSituacaoMinutaAutocomplete(): IAutocomplete<IUsuario>[] {
        const retorno = [];
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                proc.minutas.forEach(minuta => {
                    retorno[minuta.cpfUltimaAlteracao] = minuta.cpfUltimaAlteracao;
                });
            }

            const ret = Object.values(retorno).map(cpf => {
                let servidor = GabineteStore.listaUsuariosGabinete.find(usu => usu.login === cpf);
                if (!servidor) {
                    servidor = {
                        nome: cpf,
                        login: cpf
                    } as IUsuario;
                }
                return {
                    texto: servidor.nome,
                    parametrosBusca: [servidor.nome, servidor.login],
                    valor: servidor
                }
            });
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get ojAutoComplete(): IAutocomplete<string>[] {
        const ret: IAutocomplete<string>[] = [];
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                const siglaOjcProcesso = proc.orgaoJulgadorColegiado?.sigla?.trim();
                ret[siglaOjcProcesso] = {
                    texto: siglaOjcProcesso,
                    valor: siglaOjcProcesso,
                    parametrosBusca: [siglaOjcProcesso]
                };
            }

            return Object.values(ret);
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get situacaoMinutaFormatoAutoComplete(): IAutocomplete<string>[] {
        const situacoes: IAutocomplete<string>[] = [];

        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                proc.minutas.map((minuta) => {
                    const texto = minuta.situacaoMinuta?.descricao;
                    situacoes[texto] = {
                        texto: texto,
                        parametrosBusca: [texto],
                        valor: texto,
                        ordem: this.calculaOrdemSituacaoMinuta(minuta)
                    };
                });
            }
            const ret = Object.values(situacoes).sort((situacaoMinuta1, situacaoMinuta2) => situacaoMinuta1.ordem - situacaoMinuta2.ordem);;
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    calculaOrdemSituacaoMinuta(minuta) {
        switch (minuta.situacaoMinuta?.descricao) {
            case "EM ELABORAÇÃO":
                return 1;
            case "EM REVISÃO":
                return 2;
            case "PROCESSO MOVIMENTADO NO FLUXO":
                return 3;
            case "VISTO ASSINADO":
                return 4;
            case "LIBERADA PARA ASSINATURA OU LIBERADA PARA INCLUSÃO EM PAUTA":
                return 5;
            case "DEVOLVIDA PARA CORREÇÃO":
                return 6;
            case "PUBLICADA":
                return 7;
            case "ARQUIVADA":
                return 8;
            case "ASSINADA":
                return 9;
            case "PETICOES APRECIADAS":
                return 10;
            case "LANÇADOS OS MOVIMENTOS NO PROCESSO":
                return 11;
            case "MINUTA SALVA NA ORIGEM":
                return 12;
            case "ASSINATURA VERIFICADA":
                return 13;
            case "INEXISTENTE":
                return 15;
            case "BLOQUEADA PELA SECRETARIA":
                return 14;
        }
    }

    // Action que atualiza o observable
    atualizarUnidades(unidades: UnidadePublicadora[]) {
        this.unidadesDePublicacao = unidades;
    }

    get unidadePublicadoraFormatoAutoComplete(): IAutocomplete<string>[] {
        const unidades: IAutocomplete<string>[] = [];
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                proc.minutas.map((minuta) => {
                    const unidade = this.unidadesDePublicacao.find(unidade => unidade.codigo === minuta.codUnAdmPub);
                    let nomeUnidade = null;
                    unidade ? (nomeUnidade = unidade.nome) : (nomeUnidade = 'Sem unidade');
                    unidades[nomeUnidade] = {
                        texto: nomeUnidade,
                        parametrosBusca: [nomeUnidade],
                        valor: nomeUnidade,
                        ordem: this.calculaOrdemUnidadePublicadora(minuta.codUnAdmPub)
                    };
                })
            }

            const ret = Object.values(unidades).sort((unidade1, unidade2) => unidade1.ordem - unidade2.ordem);;
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    calculaOrdemUnidadePublicadora(codUnAdmPub) {
        return this.unidadesDePublicacao.findIndex(unidade => unidade.codigo === codUnAdmPub);
    }

    get etiquetasUnicasParaAutoComplete(): IAutocomplete<IEtiqueta>[] {
        let etiquetas: IAutocomplete<IEtiqueta>[] = [];
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                proc.etiquetas.forEach(etiqueta => {
                    const texto = etiqueta.texto;
                    etiquetas[texto] = {
                        valor: etiqueta,
                        texto: texto,
                        key: texto,
                        parametrosBusca: [texto]
                    }
                });

            }

            etiquetas = Object.values(etiquetas);
            const nenhuma = new TAG_NENHUMA_ETIQUETA();
            const alguma = new TAG_ALGUMA_ETIQUETA();
            etiquetas.splice(0, 0, {
                valor: nenhuma,
                texto: nenhuma.texto,
                key: nenhuma.texto,
                parametrosBusca: [nenhuma.texto]
            });
            etiquetas.splice(1, 0, {
                valor: alguma,
                texto: alguma.texto,
                key: alguma.texto,
                parametrosBusca: [alguma.texto]
            });
            return etiquetas;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get tiposPeticaoPendenteAutocomplete(): IAutocomplete<string>[] {
        const tipos: IAutocomplete<string>[] = [];
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                if (proc.peticoesPendentes?.length > 0 || proc.pecasPeticoesPendentes?.length > 0) {
                    proc.pecasPeticoesPendentes?.length > 0 && proc.pecasPeticoesPendentes.forEach(peca => {
                        tipos[peca.tipo.nomeTipoProcessoDocumento] = {
                            texto: peca.tipo.nomeTipoProcessoDocumento,
                            parametrosBusca: [peca.tipo.nomeTipoProcessoDocumento],
                            valor: peca.tipo.nomeTipoProcessoDocumento
                        };
                    });

                    proc.peticoesPendentes.length > 0 && proc.peticoesPendentes.forEach(peca => {
                        const tipo = peca.tipo || peca.titulo;
                        tipos[tipo] = {
                            texto: tipo,
                            parametrosBusca: [tipo],
                            valor: tipo
                        };
                    });
                }
            }

            let ret = Object.values(tipos)
                .sort((a, b) => a.texto.localeCompare(b.texto, 'pt-BR', { sensitivity: 'base' }));
            if (ProcessoStore.carregouDocPeticoes) {
                ret = [{
                    parametrosBusca: ['Qualquer tipo'],
                    texto: 'Qualquer tipo',
                    valor: 'Qualquer tipo'
                }].concat(ret);
            }
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get partesUnicosFormatoAutoComplete() {
        const nomes: IAutocomplete<string>[] = [];
        try {
            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                if (proc.partes) {
                    proc.partes.ATIVO && proc.partes.ATIVO.forEach(ativo => {
                        nomes[ativo.nome] = {
                            texto: ativo.nome,
                            textoSecundario: 'Polo ativo',
                            parametrosBusca: [ativo.nome, 'Polo ativo', 'ativo'],
                            valor: ativo.nome
                        };
                        ativo.representantes && ativo.representantes.forEach(representante => {
                            nomes[representante.nome] = {
                                texto: representante.nome,
                                textoSecundario: 'Polo ativo',
                                parametrosBusca: [representante.nome, 'Polo ativo', 'ativo'],
                                valor: representante.nome
                            };
                        });
                    });
                    proc.partes.PASSIVO && proc.partes.PASSIVO.forEach(passivo => {
                        nomes[passivo.nome] = {
                            texto: passivo.nome,
                            textoSecundario: 'Polo passivo',
                            parametrosBusca: [passivo.nome, 'Polo passivo', 'passivo'],
                            valor: passivo.nome
                        };
                        passivo.representantes && passivo.representantes.forEach(representante => {
                            nomes[representante.nome] = {
                                texto: representante.nome,
                                textoSecundario: 'Polo passivo',
                                parametrosBusca: [representante.nome],
                                valor: representante.nome
                            };
                        });
                    });
                    proc.partes.TERCEIROS && proc.partes.TERCEIROS.forEach(terceiro => {
                        nomes[terceiro.nome] = {
                            texto: terceiro.nome,
                            textoSecundario: 'Terceiro interessado',
                            parametrosBusca: [terceiro.nome, 'terceiro interessado', 'terceiro'],
                            valor: terceiro.nome
                        };
                        terceiro.representantes && terceiro.representantes.forEach(representante => {
                            nomes[representante.nome] = {
                                texto: representante.nome,
                                textoSecundario: 'Terceiro interessado',
                                parametrosBusca: [representante.nome, 'terceiro interessado', 'terceiro'],
                                valor: representante.nome
                            };
                        });
                    });
                }
            }
            const ret = Object.values(nomes);
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get impedimentosAutocomplete(): IAutocomplete<string>[] {
        const impedimentos: IAutocomplete<string>[] = [];
        try {
            const motivos = ParametroStore.motivosImpedimentos;
            const motivosLength = ParametroStore.motivosImpedimentos.length;
            for (let i = 0; i < motivosLength; i++) {
                const motivo = motivos[i];
                impedimentos.push({
                    texto: motivo.texto,
                    parametrosBusca: [motivo.texto],
                    valor: motivo.texto
                });
            }

            return impedimentos;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get camposDeTriagemBooleanFormatoAutoComplete(): IAutocomplete<FiltroTriagem>[] {
        try {
            let campos = [
                { descricao: 'Ausência de Trecho/Trecho Integral/Trecho no Início', valor: 'ausenciaTrecho' },
                { descricao: 'Artigo 896, §1º-A, III, da CLT e Trecho Insuficiente', valor: 'artigo896' },
                { descricao: 'Consonância com a Jurisprudência do TST/RG', valor: 'consonanciaJuris' },
                { descricao: 'Deserção Genérica', valor: 'desercaoGenerica' },
                { descricao: 'Enviar para CESJUC', valor: 'enviarSejusc' },
                { descricao: 'Enviar para a Secretaria de Precedentes', valor: 'enviarSecPrecedente' },
                { descricao: 'Execução – Desfundamentado', valor: 'sumula266' },
                { descricao: 'Execução – Interpretação do Título', valor: 'parteIncorreta' },
                { descricao: 'Impedimento', valor: 'impedimento' },
                { descricao: 'Intempestividade', valor: 'intempestividade' },
                { descricao: 'Irregularidade de Representação', valor: 'iregularidadeRepresentacao' },
                { descricao: 'Redistribuição', valor: 'mais3Temas' },
                { descricao: 'RRAG/RR', valor: 'rragRR' },
                { descricao: 'Sumaríssimo – Desfundamentado', valor: 'classeIncorreta' },
                { descricao: 'Súmula 126', valor: 'sumula126' },
                { descricao: 'Súmula 214', valor: 'sumula214' },
                { descricao: 'Súmula 218', valor: 'sumula218' },
                { descricao: 'Súmula 442', valor: 'sumula442' },
            ];
            const camposArray = EscaninhoStore.processosPorTipoEscaninho.reduce((prev, curr, _index) => {
                curr.triagem?.camposTriagem.forEach(campo => {
                    prev[campo.codCampoTriagem] = {
                        descricao: campo.descCampoTriagem,
                        valor: campo.codCampoTriagem
                    };
                });
                return prev;
            }, []);
            if (camposArray?.length > 0) {
                campos = campos.concat(Object.values(camposArray));
            }
            const ret = campos.map(campo => {
                return {
                    texto: campo.descricao,
                    parametrosBusca: [campo.descricao, campo.valor],
                    valor: campo
                };
            });
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get tiposMinutaAssinada(): IAutocomplete<any>[] {
        try {
            const campos = [
                { descricao: 'Voto', codigo: TipoMinuta.VOTO.toString() },
                { descricao: 'Decisão', codigo: TipoMinuta.DECISAO.toString() },
                { descricao: 'Despacho', codigo: TipoMinuta.DESPACHO.toString() }
            ];
            return campos.map(campo => {
                return {
                    texto: campo.descricao,
                    parametrosBusca: [campo.descricao, campo.codigo],
                    valor: campo
                };
            });
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get camposDeResponsavelPelaTriagemAutoComplete(): IAutocomplete<IUsuario>[] {

        try {
            const cpfsUnicos = [];

            const processos = EscaninhoStore.processosPorTipoEscaninho;
            const processosLength = EscaninhoStore.processosPorTipoEscaninho.length;
            for (let i = 0; i < processosLength; i++) {
                const proc = processos[i];
                if (proc.triagem?.cpfResponsavel) {
                    cpfsUnicos[proc.triagem.cpfResponsavel] = proc.triagem.cpfResponsavel;
                }
            }
            const ret = Object.values(cpfsUnicos).map(cpf => {
                const usuario = GabineteStore.listaUsuariosGabinete.find(usu => usu.login === cpf);
                if (!usuario) {
                    return {
                        texto: cpf,
                        parametrosBusca: [cpf],
                        valor: { login: cpf, nome: cpf } as IUsuario
                    }
                }
                return {
                    texto: usuario.nome,
                    parametrosBusca: [usuario.nome, usuario.login],
                    valor: usuario
                }
            });
            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get assuntosUnicosFormatoAutoComplete(): IAutocomplete<string>[] {
        const assuntos: IAutocomplete<string>[] = [];
        try {
            const procTipoEscaninhoLength = EscaninhoStore.processosPorTipoEscaninho.length
            for (let i = 0; i < procTipoEscaninhoLength; i++) {
                const proc = EscaninhoStore.processosPorTipoEscaninho[i];
                if (!proc.assuntosProcesso) {
                    continue;
                }
                const assuntosLength = proc.assuntosProcesso.length;
                for (let j = 0; j < assuntosLength; j++) {
                    const assunto = proc.assuntosProcesso[j];
                    const desc = assunto.descricao;
                    const descCompleta = assunto.descricaoCompleta;
                    const cod = assunto.codigoAssunto;
                    assuntos[desc] = {
                        valor: desc,
                        texto: cod ? (cod + ' - ' + desc) : desc,
                        textoSecundario: descCompleta,
                        parametrosBusca: [desc, descCompleta, cod]
                    };
                }
            }
            const ret = Object.values(assuntos);

            return ret;
        } catch (err) {
            console.error("Erro no filtro: ", err);
            return [];
        }
    }

    get tarefasAutoComplete() {
        return ControleGabineteStore.listaDeControles.map(controle => {
            const retorno: IAutocomplete<ControleGabinete> = {
                texto: controle.descricao,
                valor: controle,
                parametrosBusca: [controle.descricao]
            };
            return retorno;
        }) || [];
    }

    get usuariosAutoComplete() {
        return GabineteStore.listaUsuariosGabinete.map(usuario => {
            const retorno: IAutocomplete<string> = {
                texto: this.capitalizeWords(usuario.nome.toLowerCase()),
                valor: this.capitalizeWords(usuario.login.toLowerCase()),
                parametrosBusca: [this.capitalizeWords(usuario.nome), this.capitalizeWords(usuario.login.toLowerCase())]
            };
            return retorno;
        });
    }

    get relatorAutoComplete() {
        const relatoresUnicos = [
            ...new Set(
                ProcessoStore._processos
                    .map(p => p.orgaoJulgador?.sigla)
                    .filter(Boolean)
            )
        ];

        return relatoresUnicos.map(nome => ({
            texto: nome,
            valor: nome,
            parametrosBusca: [nome]
        }));
    }

    capitalizeWords(str) {
        const arr = str.split(" ");

        //loop through each element of the array and capitalize the first letter.
        for (let i = 0; i < arr.length; i++) {
            arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);

            if (arr[i] == "De" || arr[i] == "Da" || arr[i] == "E" || arr[i] == "Do" || arr[i] == "Dos") {
                arr[i] = arr[i].charAt(0).toLowerCase() + arr[i].slice(1);
            }
        }

        return arr.join(" ");
    }

    get indicadoresAutoComplete() {
        const indicadoresPorDescricao = new Map<string, { possuiChipPje: boolean, possuiIndicador: boolean }>();

        EscaninhoStore.processosPorTipoEscaninho
            .flatMap(proc => proc.indicadores)
            .filter(ind => ind?.descricao)
            .forEach((indicador: IIndicadores) => {
                const item = indicadoresPorDescricao.get(indicador.descricao) || { possuiChipPje: false, possuiIndicador: false };
                if (indicador.isEtiqueta) {
                    item.possuiChipPje = true;
                } else {
                    item.possuiIndicador = true;
                }
                indicadoresPorDescricao.set(indicador.descricao, item);
            });

        return Array.from(indicadoresPorDescricao.entries()).map(([descricao, tipo]) => {
            const possuiAmbos = tipo.possuiChipPje && tipo.possuiIndicador;
            const possuiChipPje = tipo.possuiChipPje;
            const retorno: IAutocomplete<string> = {
                texto: descricao,
                valor: descricao,
                parametrosBusca: [descricao],
                textColor: possuiAmbos ? 'orange' : possuiChipPje ? 'green' : 'blue',
                textoSecundario: possuiAmbos ? 'Chip PJe / Indicador' : possuiChipPje ? 'Chip PJe' : 'Indicador'
            }
            return retorno;
        }).sort((a, b) => a.texto.localeCompare(b.texto, 'pt-BR', { sensitivity: 'base' }))
    }

    get andTramTarefas() {
        const listaOpcoes = [];

        EscaninhoStore.processosPorTipoEscaninho.forEach((proc) => {

            if (!proc?.mostrar) return;

            const tarefaFluxo = proc?.tarefaFluxoPje?.tarefaAtual;

            if (tarefaFluxo && !listaOpcoes.some(a => a?.texto === tarefaFluxo)) {
                listaOpcoes.push({
                    valor: tarefaFluxo,
                    texto: tarefaFluxo,
                    key: tarefaFluxo,
                    parametrosBusca: [tarefaFluxo]
                });
            }

            const tarefaPauta = proc?.situacaoDeInclusaoEmPauta;

            if (tarefaPauta && !listaOpcoes.some(a => a?.texto === tarefaPauta)) {
                listaOpcoes.push({
                    valor: tarefaPauta,
                    texto: tarefaPauta,
                    key: tarefaPauta,
                    parametrosBusca: [tarefaPauta]
                });
            }

            const movimentoCNJ = proc.ultimoMovimentoCNJ;

            if (movimentoCNJ && !listaOpcoes.some(a => a?.texto === movimentoCNJ)) {
                listaOpcoes.push({
                    valor: movimentoCNJ,
                    texto: movimentoCNJ,
                    key: movimentoCNJ,
                    parametrosBusca: [movimentoCNJ]
                });
            }
        });

        return listaOpcoes || [];
    }

    get anoAutuacao() {

        const ar = [];

        EscaninhoStore.processosPorTipoEscaninho.forEach(proc => {
            if (proc?.dataAutuacaoTst) {
                const dataAutuacaoTst = new Date(proc?.dataAutuacaoTst)
                const jaPossui = ar.some(a => a.valor == dataAutuacaoTst?.getFullYear()?.toString());
                if (!jaPossui) {
                    ar.push({
                        valor: dataAutuacaoTst?.getFullYear()?.toString(),
                        texto: dataAutuacaoTst?.getFullYear()?.toString(),
                        key: dataAutuacaoTst?.getFullYear()?.toString(),
                        parametrosBusca: [dataAutuacaoTst?.getFullYear()?.toString()]
                    });
                }
            }

        });

        return ar?.sort((a, b) => {
            const fa = parseInt(a?.valor),
                fb = parseInt(b?.valor);

            if (fa < fb) {
                return -1;
            }
            if (fa > fb) {
                return 1;
            }
            return 0;
        }) || [];
    }
}

export default new FiltroAutoCompleteStore();
